import { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Twitter, Youtube } from "lucide-react";
import { api, img } from "@/lib/api";
import { toast } from "sonner";

export default function Footer() {
  const [email, setEmail] = useState("");

  const subscribe = async (e) => {
    e.preventDefault();
    try {
      await api.post("/newsletter", { email });
      toast.success("Welcome to the Noir List.");
      setEmail("");
    } catch {
      toast.error("Enter a valid email.");
    }
  };

  return (
    <footer className="border-t border-white/10 bg-noir-bg" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <img src={img("logo-crest.png")} alt="Jae Noir crest" className="h-10 w-10 object-contain" />
              <span className="font-display font-extrabold tracking-[0.25em] uppercase text-lg">Jae Noir</span>
            </div>
            <p className="text-cream/50 text-sm leading-relaxed max-w-sm">
              Built in silence. Move in faith. Leave a legacy. Premium heavyweight streetwear for those who let their results speak.
            </p>
            <form onSubmit={subscribe} className="mt-8 flex max-w-sm" data-testid="footer-newsletter-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email for the Noir List"
                className="flex-1 bg-transparent border border-white/15 border-r-0 px-4 py-3 text-sm placeholder:text-cream/30 focus:outline-none focus:border-gold/60 transition-colors"
                data-testid="footer-noir-list-email-input"
              />
              <button type="submit" className="bg-gold text-noir-bg px-5 font-mono2 text-[10px] tracking-[0.2em] uppercase hover:bg-cream transition-colors duration-300" data-testid="footer-noir-list-submit-button">
                Join
              </button>
            </form>
          </div>

          <div className="md:col-span-2">
            <h4 className="font-mono2 text-[10px] tracking-[0.3em] uppercase text-gold mb-5">Shop</h4>
            <ul className="space-y-3 text-sm text-cream/60">
              <li><Link to="/shop?drop=new" className="hover:text-gold transition-colors" data-testid="footer-shop-new-drop">New Drop</Link></li>
              <li><Link to="/shop" className="hover:text-gold transition-colors" data-testid="footer-shop-all">All Products</Link></li>
              <li><Link to="/shop?category=Tees" className="hover:text-gold transition-colors" data-testid="footer-shop-tees">Tees</Link></li>
              <li><Link to="/shop?category=Hoodies" className="hover:text-gold transition-colors" data-testid="footer-shop-hoodies">Hoodies</Link></li>
              <li><Link to="/shop?category=Accessories" className="hover:text-gold transition-colors" data-testid="footer-shop-accessories">Accessories</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <h4 className="font-mono2 text-[10px] tracking-[0.3em] uppercase text-gold mb-5">Brand</h4>
            <ul className="space-y-3 text-sm text-cream/60">
              <li><Link to="/story" className="hover:text-gold transition-colors" data-testid="footer-story">Our Story</Link></li>
              <li><Link to="/lookbook" className="hover:text-gold transition-colors" data-testid="footer-lookbook">Lookbook</Link></li>
              <li><Link to="/collections" className="hover:text-gold transition-colors" data-testid="footer-collections">Collections</Link></li>
              <li><Link to="/contact" className="hover:text-gold transition-colors" data-testid="footer-contact">Contact</Link></li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <h4 className="font-mono2 text-[10px] tracking-[0.3em] uppercase text-gold mb-5">Support</h4>
            <ul className="space-y-3 text-sm text-cream/60">
              <li><Link to="/track" className="hover:text-gold transition-colors" data-testid="footer-track-order">Track Order</Link></li>
              <li><Link to="/info?tab=faq" className="hover:text-gold transition-colors" data-testid="footer-faq">FAQ</Link></li>
              <li><Link to="/info?tab=shipping" className="hover:text-gold transition-colors" data-testid="footer-shipping">Shipping & Returns</Link></li>
              <li><Link to="/info?tab=privacy" className="hover:text-gold transition-colors" data-testid="footer-privacy">Privacy Policy</Link></li>
              <li><Link to="/info?tab=terms" className="hover:text-gold transition-colors" data-testid="footer-terms">Terms of Service</Link></li>
            </ul>
            <div className="flex gap-4 mt-6">
              <a href="https://instagram.com" target="_blank" rel="noreferrer" className="text-cream/50 hover:text-gold transition-colors" data-testid="social-instagram" aria-label="Instagram"><Instagram size={18} strokeWidth={1.5} /></a>
              <a href="https://twitter.com" target="_blank" rel="noreferrer" className="text-cream/50 hover:text-gold transition-colors" data-testid="social-twitter" aria-label="Twitter"><Twitter size={18} strokeWidth={1.5} /></a>
              <a href="https://youtube.com" target="_blank" rel="noreferrer" className="text-cream/50 hover:text-gold transition-colors" data-testid="social-youtube" aria-label="YouTube"><Youtube size={18} strokeWidth={1.5} /></a>
            </div>
          </div>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row justify-between gap-3 items-center">
          <p className="font-mono2 text-[10px] tracking-[0.3em] uppercase text-cream/30">© 2026 Jae Noir. All rights reserved.</p>
          <p className="font-mono2 text-[10px] tracking-[0.3em] uppercase text-gold/50">Move Different. Leave a Legacy.</p>
        </div>
      </div>
    </footer>
  );
}
